import * as React from 'react';
import { IntlProvider, FormattedNumber } from 'react-intl';
import { FormattedDateTime } from '../DataDisplay/FormattedDateTime';
import { FormattedCurrency, CurrencyDisplayStyle } from '../DataDisplay/FormattedCurrency';
import { BorderedInfoTableField } from '../DataDisplay/BorderedInfoTable';

export var DataType = {
    Default: 0,     // string
    String: 1,      // to verify
    Date: 2,
    Number: 3,       // use the formatter
    Currency: 4,
    Boolean: 5,
    DateTime: 6,
    Image: 7,
    Percentage: 8,
    ContextMenu: 9,
    Link: 10,
    LongText: 11,
    Checkbox: 12,
    CurrencyWithoutDecimals: 13,
    EditorView: 14,
    Duration: 15,
    DueDate: 16,
    Chips: 17,
    ContextMenuStaff: 18,
    Rating: 19,
    EventCampaignCategory: 20,
    MultiLineString: 21,
    Email: 22
};

interface DisplayModel {
    stringFormat: string;
    dataType: number; // use the DataTopicType exported from RvDataTopicInfo
    currencyDisplayStyle?: CurrencyDisplayStyle;
}

// Explicit request: give the Other Information tab the same bordered label:value table
// treatment as the rest of Matter Details — this now returns a plain {label, value} field
// for BorderedInfoTable instead of a pre-composed Grid-based row.
export function labelAndValueFormatter(dataType: number, label: string, value: string | Date,
                                        dateFormat?: string, idx?: string): BorderedInfoTableField {
    void dateFormat;
    void idx;

    let displayModel: DisplayModel = dateFormat ? getDataFormat(dateFormat) : getDataFormat('');

    let numericValue: number = value ? Number.parseFloat(value as string) : 0;

    switch (dataType) {
        case DataType.Default:
        case DataType.String:
        default:
            return { label, value: value as string };
        case DataType.Number:
            if (displayModel && displayModel.currencyDisplayStyle) {
                return { label, value: <FormattedCurrency value={numericValue} displayStyle={displayModel.currencyDisplayStyle} /> };
            } else {
                return {
                    label,
                    value: (
                        <IntlProvider locale="en">
                            <FormattedNumber value={numericValue} minimumFractionDigits={0} />
                        </IntlProvider>
                    ),
                };
            }
        case DataType.Date:
        case DataType.DateTime:
            return { label, value: <FormattedDateTime value={value as Date} /> };
        case DataType.Currency:
            return { label, value: <FormattedCurrency value={numericValue} displayStyle={CurrencyDisplayStyle.Decimal} /> };
    }
}

const getDataFormat = (param: string): DisplayModel => {
    let returnValue: DisplayModel = {
        stringFormat: '',
        dataType: DataType.String,
        currencyDisplayStyle: CurrencyDisplayStyle.Decimal
    };
    switch (param) {
        case 'N1':
            returnValue = {
                stringFormat: '0:###,###,###.00', dataType: DataType.Number,
                currencyDisplayStyle: CurrencyDisplayStyle.Decimal
            };
            break;
        case 'N2':
            returnValue = {
                stringFormat: '0:###,###,###', dataType: DataType.Number,
                currencyDisplayStyle: CurrencyDisplayStyle.Default
            };
            break;
        case 'N3':
            returnValue = {
                stringFormat: '0:###,###,###.00', dataType: DataType.Number,
                currencyDisplayStyle: CurrencyDisplayStyle.Decimal
            };
            break;
        case 'N4':
            returnValue = {
                stringFormat: '0:###,###,###', dataType: DataType.Number,
                currencyDisplayStyle: CurrencyDisplayStyle.Custom
            };
            break;
        case 'N5':
            returnValue = {
                stringFormat: '0:###,###,###.00;###,###,###.00',
                dataType: DataType.Number, currencyDisplayStyle: CurrencyDisplayStyle.Decimal
            };
            break;
        case 'N6':
            returnValue = {
                stringFormat: '0:###,###,###.00;###,###,###.00',
                dataType: DataType.Number, currencyDisplayStyle: CurrencyDisplayStyle.Decimal
            };
            break;
        case 'N7':
            returnValue = {
                stringFormat: '0:###,###,###.00;###,###,###.00;Nil',
                dataType: DataType.Number, currencyDisplayStyle: CurrencyDisplayStyle.Decimal
            };
            break;
        case 'N8':
            returnValue = {
                stringFormat: '0:###,###,###.00;###,###,###.00;Nil',
                dataType: DataType.Number, currencyDisplayStyle: CurrencyDisplayStyle.Decimal
            };
            break;
        case 'N9':
            returnValue = { stringFormat: '', dataType: DataType.Number };
            break;
        case 'N10':
            returnValue = { stringFormat: '', dataType: DataType.Number };
            break;
        case 'N11':
            returnValue = {
                stringFormat: '0:###,###,###.00', dataType: DataType.Number,
                currencyDisplayStyle: CurrencyDisplayStyle.Decimal
            };
            break;
        case 'N12':
            returnValue = { stringFormat: '', dataType: DataType.Number };
            break;
        case 'N13':
            returnValue = { stringFormat: '', dataType: DataType.Number };
            break;
        case 'N14':
            returnValue = { stringFormat: '', dataType: DataType.Number };
            break;
        case 'N15':
            returnValue = { stringFormat: '', dataType: DataType.Number };
            break;

        case 'D1':
            returnValue = { stringFormat: '0:dd/MM/yy', dataType: DataType.Date };
            break;
        case 'D2':
            returnValue = { stringFormat: '0:dddd, dd MMM yy', dataType: DataType.Date };
            break;
        case 'D3':
            returnValue = { stringFormat: '0:dd MMM yyyy', dataType: DataType.Date };
            break;
        case 'D4':
            returnValue = { stringFormat: '0:ddd dd MM yy', dataType: DataType.Date };
            break;
        case 'D5':
            returnValue = { stringFormat: '0:dd MMM yy', dataType: DataType.Date };
            break;
        case 'D6':
            returnValue = { stringFormat: '0:dd/MM/yy', dataType: DataType.Date };
            break;
        case 'D7':
            returnValue = { stringFormat: '0:dddd, dd MMMM yyyy', dataType: DataType.Date };
            break;
        case 'D8':
            returnValue = { stringFormat: '0:dd MMMM yyyy', dataType: DataType.Date };
            break;
        default:
            returnValue = {
                stringFormat: '0:###,###,###.00', dataType: DataType.Number,
                currencyDisplayStyle: CurrencyDisplayStyle.Decimal
            };
            break;
    }

    return returnValue;
};
